import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import configService from "../services/config";
import {
  Box, Button, Chip, CircularProgress, Dialog, Divider, IconButton,
  InputAdornment, MenuItem, Paper, Stack, TextField, Tooltip, Typography,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const STATUS_STYLE = {
  Pending:     { bg: "#FBF0DA", fg: "#8A5A0E", dot: "#C98A2C" },
  "In Review": { bg: "#DCE8F5", fg: "#1F4E85", dot: "#2563A6" },
  Resolved:    { bg: "#DFEEE5", fg: "#1F5C41", dot: "#2F7D5C" },
};

const PRIORITY_STYLE = {
  Low:    { fg: "#5A6B62", bg: "#E4E9E5" },
  Medium: { fg: "#8A5A0E", bg: "#FBF0DA" },
  High:   { fg: "#A6321F", bg: "#F7DEDA" },
};

/* ------------------------------------------------------------------ */
/* Theme                                                               */
/* ------------------------------------------------------------------ */

const appTheme = createTheme({
  palette: {
    primary: { main: "#16302B", contrastText: "#EFE6C9" },
    secondary: { main: "#A97C1F" },
    background: { default: "#EEF1EE", paper: "#FFFFFF" },
    text: { primary: "#16302B", secondary: "#5A6B62" },
  },
  typography: { fontFamily: "Inter, -apple-system, 'Segoe UI', sans-serif" },
  shape: { borderRadius: 8 },
});

const serif = "'Roboto Slab', Georgia, serif";

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function getFileType(url = "", name = "") {
  const target = (url || name).toLowerCase().split("?")[0];
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/.test(target)) return "image";
  if (/\.pdf$/.test(target)) return "pdf";
  return "other";
}

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export default function ComplaintTrack({ role = "Resident", onNavigate }) {
  const { company_id, userId } = useSelector((state) => state.globalVariables);

  /* -------- Categories -------- */
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    if (!company_id) return;
    (async () => {
      try {
        const res = await configService.complaintCategories({ company_id });
        const list =
          (Array.isArray(res?.data) && res.data) ||
          (Array.isArray(res?.data?.data) && res.data.data) ||
          [];
        setCategories(list);
      } catch (err) {
        console.error("❌ Categories fetch error:", err);
      }
    })();
  }, [company_id]);

  /* -------- Complaints -------- */
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchId, setSearchId] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [categoryFilter, setCategoryFilter] = useState("All");

  const fetchComplaints = useCallback(async () => {
    if (!company_id) return;

    setLoading(true);
    try {
      const res = await configService.complaintTrack({
        company_id,
        customer_id: userId,
        role,
        search_id: searchId.trim(),
        status: statusFilter,
        job_category_id: categoryFilter === "All" ? "" : categoryFilter,
      });

      const items =
        (Array.isArray(res?.data) && res.data) ||
        (Array.isArray(res?.data?.data) && res.data.data) ||
        [];

      setComplaints(items);
    } catch (error) {
      console.error("❌ Complaint Track Error:", error);
      toast.error(
        `Error: ${error?.response?.data?.message || error?.message || "Server Error"}`
      );
      setComplaints([]);
    } finally {
      setLoading(false);
    }
  }, [company_id, userId, role, searchId, statusFilter, categoryFilter]);

  useEffect(() => {
    const t = setTimeout(() => fetchComplaints(), 350);
    return () => clearTimeout(t);
  }, [fetchComplaints]);

  /* -------- Attachment preview -------- */
  const [preview, setPreview] = useState(null);

  const openPreview = (c) => {
    const url = c.attachment_url;
    const name = c.attachment || "";

    if (!url) {
      toast.error("Attachment URL missing.");
      return;
    }
    const type = getFileType(url, name);
    setPreview({ url, name, type });
  };

  const closePreview = () => setPreview(null);

  /* -------- Render -------- */
  const statusOptions = ["All", "Pending", "In Review", "Resolved"];

  return (
    <ThemeProvider theme={appTheme}>
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          display: "flex",
          justifyContent: "center",
          px: 2,
          py: 4,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 560 }}>
          <Paper
            elevation={0}
            sx={{ border: "1px solid #D8DFDA", borderRadius: 2, overflow: "hidden" }}
          >
            {/* ❌ Header removed — koi "Work Sphere" / company_name nahi */}

            <Box sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                {/* Search */}
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by complaint ID (e.g. CMP-2026-0001)"
                  value={searchId}
                  onChange={(e) => setSearchId(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Filters */}
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    select size="small" label="Status" fullWidth
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    {statusOptions.map((s) => (
                      <MenuItem key={s} value={s}>{s}</MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    select size="small" label="Category" fullWidth
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                  >
                    <MenuItem value="All">All</MenuItem>
                    {categories.map((c) => (
                      <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                    ))}
                  </TextField>
                </Stack>

                {/* Count */}
                <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                  {loading
                    ? "Loading..."
                    : `${complaints.length} complaint${complaints.length === 1 ? "" : "s"} found`}
                </Typography>

                <Divider />

                {/* List */}
                {loading ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={28} sx={{ color: "#16302B" }} />
                  </Box>
                ) : complaints.length === 0 ? (
                  <Typography
                    sx={{
                      fontSize: 13.5, color: "text.secondary",
                      py: 2, textAlign: "center",
                    }}
                  >
                    No complaint matches your search or filters.
                  </Typography>
                ) : (
                  <Stack spacing={1.25}>
                    {complaints.map((c) => {
                      const s = STATUS_STYLE[c.status] || STATUS_STYLE.Pending;
                      const p = PRIORITY_STYLE[c.priority] || PRIORITY_STYLE.Medium;

                      return (
                        <Paper
                          key={c.id}
                          variant="outlined"
                          sx={{
                            p: 1.5, borderRadius: 2,
                            bgcolor: "#FCFCFB", borderColor: "#E4E9E5",
                          }}
                        >
                          <Stack spacing={0.75}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              spacing={1}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography
                                  sx={{ fontFamily: serif, fontWeight: 700, fontSize: 13.5 }}
                                >
                                  {c.complaint_id || c.id}
                                </Typography>
                                <Typography
                                  sx={{ fontSize: 12, color: "text.secondary", mt: 0.25 }}
                                >
                                  {c.name} · {c.category}
                                </Typography>
                              </Box>

                              <Stack direction="row" spacing={0.5} flexShrink={0}>
                                <Chip
                                  size="small"
                                  label={c.priority}
                                  sx={{
                                    bgcolor: p.bg, color: p.fg, fontWeight: 700,
                                    fontSize: 10.5, height: 20,
                                  }}
                                />
                                <Chip
                                  size="small"
                                  label={c.status}
                                  sx={{
                                    bgcolor: s.bg, color: s.fg, fontWeight: 700,
                                    fontSize: 10.5, height: 20,
                                    "& .MuiChip-label": {
                                      display: "flex", alignItems: "center", gap: 0.5,
                                    },
                                  }}
                                  icon={
                                    <Box
                                      component="span"
                                      sx={{
                                        width: 6, height: 6, borderRadius: "50%",
                                        bgcolor: s.dot, ml: 0.75,
                                      }}
                                    />
                                  }
                                />
                              </Stack>
                            </Stack>

                            {c.desc && (
                              <Typography
                                sx={{
                                  fontSize: 12.5, color: "text.secondary",
                                  overflow: "hidden", textOverflow: "ellipsis",
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
                                }}
                              >
                                {c.desc}
                              </Typography>
                            )}

                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography sx={{ fontSize: 11, color: "#9AA6A0" }}>
                                {c.date}
                              </Typography>

                              {/* Attachment chip — CLICKABLE */}
                              {c.attachment && c.attachment_url ? (
                                <Button
                                  size="small"
                                  onClick={() => openPreview(c)}
                                  startIcon={<AttachFileRoundedIcon sx={{ fontSize: 14 }} />}
                                  sx={{
                                    textTransform: "none",
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: "#16302B",
                                    bgcolor: "#F3F0E4",
                                    px: 1, py: 0.25,
                                    minWidth: 0,
                                    borderRadius: 1,
                                    "&:hover": { bgcolor: "#E6DEC2" },
                                  }}
                                >
                                  <Box
                                    component="span"
                                    sx={{
                                      maxWidth: 160,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {c.attachment}
                                  </Box>
                                </Button>
                              ) : c.attachment ? (
                                <Typography sx={{ fontSize: 11, color: "#9AA6A0" }}>
                                  📎 {c.attachment}
                                </Typography>
                              ) : null}
                            </Stack>
                          </Stack>
                        </Paper>
                      );
                    })}
                  </Stack>
                )}
              </Stack>
            </Box>
          </Paper>

          {onNavigate && (
            <Button
              fullWidth
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => onNavigate("register")}
              sx={{ mt: 1.5, textTransform: "none", fontWeight: 700 }}
            >
              Register a new complaint
            </Button>
          )}
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* Attachment Preview Dialog — 80% screen, top-right close button   */}
        {/* ---------------------------------------------------------------- */}
        <Dialog
          open={Boolean(preview)}
          onClose={closePreview}
          maxWidth={false}
          fullWidth
          PaperProps={{
            sx: {
              width: "80vw",
              height: "80vh",
              maxWidth: "80vw",
              maxHeight: "80vh",
              m: 0,
              borderRadius: 2,
              overflow: "hidden",
              bgcolor: "#0F1412",
              position: "relative",
            },
          }}
          sx={{
            "& .MuiDialog-container": {
              alignItems: "center",
              justifyContent: "center",
            },
          }}
        >
          {/* Header strip with close button */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{
              px: 2,
              py: 1,
              bgcolor: "rgba(0,0,0,0.6)",
              color: "#EFE6C9",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              flexShrink: 0,
            }}
          >
            <AttachFileRoundedIcon sx={{ fontSize: 16, opacity: 0.85 }} />
            <Typography
              sx={{
                flex: 1,
                fontSize: 13,
                fontWeight: 600,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {preview?.name || "Attachment"}
            </Typography>

            {preview?.url && (
              <Tooltip title="Open in new tab">
                <IconButton
                  size="small"
                  component="a"
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "#EFE6C9" }}
                >
                  <OpenInNewRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Close">
              <IconButton
                size="small"
                onClick={closePreview}
                sx={{
                  color: "#EFE6C9",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                }}
              >
                <CloseRoundedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: "hidden",
              bgcolor: "#0F1412",
            }}
          >
            {!preview ? null : preview.type === "image" ? (
              <Box
                component="img"
                src={preview.url}
                alt={preview.name}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            ) : preview.type === "pdf" ? (
              <Box
                component="iframe"
                src={preview.url}
                title={preview.name}
                sx={{
                  width: "100%",
                  height: "100%",
                  border: 0,
                  bgcolor: "#fff",
                }}
              />
            ) : (
              <Stack spacing={2} alignItems="center" sx={{ p: 4, color: "#EFE6C9" }}>
                <Typography sx={{ fontSize: 14 }}>
                  Preview not supported for this file type.
                </Typography>
                <Button
                  variant="contained"
                  component="a"
                  href={preview.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Open / Download
                </Button>
              </Stack>
            )}
          </Box>
        </Dialog>
      </Box>
    </ThemeProvider>
  );
}